"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  GameSearchResult,
  PublisherSearchResult,
  SearchResults,
} from "./types";
import {
  SEARCH_DEBOUNCE_MS,
  MIN_QUERY_LENGTH,
  MAX_GAMES_RESULTS,
  MAX_PUBLISHERS_RESULTS,
} from "./constants";
import { capture } from "@/lib/analytics/posthog";

type GameSearchRow = {
  appid: number;
  name: string;
  capsule_image?: string | null;
  is_free?: boolean | null;
  price_final?: number | null;
  price_initial?: number | null;
  price_discount?: number | null;
  price_final_formatted?: string | null;
  price_initial_formatted?: string | null;
};

type PublisherSearchRow = {
  clanid: number;
  name: string;
  avatar?: string | null;
  backgroundHeader?: string | null;
  gamesCount?: number | null;
};

export function useSearch() {
  const [query, setQuery] = useState("");
  const [gamesResults, setGamesResults] = useState<GameSearchResult[]>([]);
  const [publishersResults, setPublishersResults] = useState<
    PublisherSearchResult[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [expandedGames, setExpandedGames] = useState(false);
  const [expandedPublishers, setExpandedPublishers] = useState(false);

  const requestSeq = useRef(0);
  const cacheRef = useRef(new Map<string, SearchResults>());

  const clearResults = useCallback(() => {
    requestSeq.current += 1;
    setGamesResults([]);
    setPublishersResults([]);
    setExpandedGames(false);
    setExpandedPublishers(false);
    setLoading(false);
  }, []);

  const clearQuery = useCallback(() => {
    setQuery("");
    clearResults();
  }, [clearResults]);

  useEffect(() => {
    const q = query.trim();

    // Allow search by numeric ID even if it's shorter than MIN_QUERY_LENGTH
    const isNumericId = Number.isFinite(Number(q)) && Number(q) > 0;

    if (q.length < MIN_QUERY_LENGTH && !isNumericId) {
      clearResults();
      return;
    }

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      const cached = cacheRef.current.get(q);
      if (cached) {
        setGamesResults(cached.games);
        setPublishersResults(cached.publishers);
        setLoading(false);
        return;
      }

      const seq = ++requestSeq.current;
      setLoading(true);

      try {
        // Dynamic import щоб уникнути конфлікту з auth init при залогіненому юзері
        const [{ searchGamesClient }, { searchPublishersClient }] =
          await Promise.all([
            import("@/features/games/api/searchGamesClient"),
            import("@/features/publishers/api/searchPublishersClient"),
          ]);
        const [gamesData, publishersData] = await Promise.all([
          searchGamesClient(q, MAX_GAMES_RESULTS, controller.signal),
          searchPublishersClient(q, MAX_PUBLISHERS_RESULTS, controller.signal),
        ]);

        if (seq !== requestSeq.current) return;

        const games: GameSearchResult[] = (gamesData as GameSearchRow[]).map(
          (g) => ({
            type: "game" as const,
            appid: Number(g.appid),
            name: String(g.name ?? ""),
            image: g.capsule_image ?? null,
            isFree: g.is_free ?? null,
            priceFinal: g.price_final ?? null,
            priceInitial: g.price_initial ?? null,
            priceDiscount: g.price_discount ?? null,
            priceFinalFormatted: g.price_final_formatted ?? null,
            priceInitialFormatted: g.price_initial_formatted ?? null,
          }),
        );

        const publishers: PublisherSearchResult[] = (
          publishersData as PublisherSearchRow[]
        ).map((p) => ({
          type: "publisher" as const,
          clanid: Number(p.clanid),
          name: String(p.name ?? ""),
          avatar: p.avatar ?? null,
          backgroundHeader: p.backgroundHeader ?? null,
          gamesCount: p.gamesCount ?? null,
        }));

        const cache = cacheRef.current;
        if (cache.size >= 50) {
          // Evict oldest entry
          const firstKey = cache.keys().next().value;
          if (firstKey !== undefined) cache.delete(firstKey);
        }
        cache.set(q, { games, publishers });
        // Reported on a real network result only — a cache hit (above) is the
        // same search re-shown, and the raw query never leaves the browser,
        // only its length.
        capture("search performed", {
          query_length: q.length,
          source: "header",
          game_results: games.length,
          publisher_results: publishers.length,
        });
        setGamesResults(games);
        setPublishersResults(publishers);
        setExpandedGames(false);
        setExpandedPublishers(false);
      } finally {
        if (seq === requestSeq.current) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      // Cancel any in-flight request for the previous query so we don't keep
      // a stale fetch (and its server-side BotID/DB work) running after the
      // user has typed more.
      controller.abort();
    };
  }, [query, clearResults]);

  return {
    query,
    setQuery,
    gamesResults,
    publishersResults,
    loading,
    expandedGames,
    setExpandedGames,
    expandedPublishers,
    setExpandedPublishers,
    clearQuery,
    clearResults,
  };
}
